
import React, { memo } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

interface EnrollmentDataItem {
  name: string;
  enrollments: number;
}

interface EnrollmentTrendsChartProps {
  data: EnrollmentDataItem[];
  loading: boolean;
}

// Performance optimized chart component with enhanced animations
const EnrollmentTrendsChart = memo(({ data, loading }: EnrollmentTrendsChartProps) => {
  // Check for reduced motion preference
  const isAnimationActive = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (loading) {
    return (
      <div className="h-[300px] w-full space-y-3">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          No enrollment data available
        </div>
      </div>
    );
  }

  return (
    <div className="h-[300px] w-full animate-fade-in">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis 
            dataKey="name" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            width={30}
            // Using inline props instead of defaultProps
          />
          <Tooltip 
            cursor={{ fill: 'rgba(61, 112, 105, 0.1)' }}
            contentStyle={{ 
              borderRadius: '8px', 
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
          <Bar 
            dataKey="enrollments" 
            fill="#3D7069"
            isAnimationActive={isAnimationActive}
            animationDuration={800}
            animationBegin={0}
            animationEasing="ease-out"
            radius={[4, 4, 0, 0]}
            className="hover:opacity-90 transition-opacity"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
});

EnrollmentTrendsChart.displayName = 'EnrollmentTrendsChart';

export default EnrollmentTrendsChart;
